import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

interface TipsFilters {
    locationName: string;
    statusFilter: string;
    reportingStatusFilter: string;
    addressFilter: string;
}

interface TipsFiltersHandlers {
    setLocationName: (value: string) => void;
    setStatusFilter: (value: string) => void;
    setReportingStatusFilter: (value: string) => void;
    setAddressFilter: (value: string) => void;
}

interface UseTipsFiltersReturn extends TipsFilters, TipsFiltersHandlers {
    filtersKey: string; // Used to detect filter changes for page reset
}

/**
 * Custom hook to manage tips filters via URL search params
 * Provides synchronized state management between URL and component state
 */
export const useTipsFilters = (): UseTipsFiltersReturn => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Parse string parameter from URL
    const getStringParam = useCallback(
        (key: string, defaultValue: string = 'all'): string => {
            return searchParams.get(key) || defaultValue;
        },
        [searchParams]
    );

    // Initialize all filter states from URL
    const [locationName, setLocationNameState] = useState<string>(() =>
        getStringParam('location', 'all')
    );

    const [statusFilter, setStatusFilterState] = useState<string>(() =>
        getStringParam('status', 'all')
    );

    const [reportingStatusFilter, setReportingStatusFilterState] =
        useState<string>(() => getStringParam('reportingStatus', 'all'));

    const [addressFilter, setAddressFilterState] = useState<string>(() =>
        getStringParam('address', '')
    );

    // Update URL params when filters change
    const updateUrlParam = useCallback(
        (key: string, value: string) => {
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev);
                    if (value && value !== 'all' && value !== '') {
                        newParams.set(key, value);
                    } else {
                        newParams.delete(key);
                    }
                    return newParams;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // Sync state when URL changes (browser navigation, reset, etc.)
    useEffect(() => {
        setLocationNameState(getStringParam('location', 'all'));
        setStatusFilterState(getStringParam('status', 'all'));
        setReportingStatusFilterState(getStringParam('reportingStatus', 'all'));
        setAddressFilterState(getStringParam('address', ''));
    }, [searchParams, getStringParam]);

    // Create a stable key that changes when any filter changes
    // This can be used to trigger page resets
    const filtersKey = useMemo(() => {
        return [
            locationName,
            statusFilter,
            reportingStatusFilter,
            addressFilter
        ].join('|');
    }, [locationName, statusFilter, reportingStatusFilter, addressFilter]);

    // Wrapper setters that also update URL
    const setLocationName = useCallback(
        (value: string) => {
            setLocationNameState(value);
            updateUrlParam('location', value);
        },
        [updateUrlParam]
    );

    const setStatusFilter = useCallback(
        (value: string) => {
            setStatusFilterState(value);
            updateUrlParam('status', value);
        },
        [updateUrlParam]
    );

    const setReportingStatusFilter = useCallback(
        (value: string) => {
            setReportingStatusFilterState(value);
            updateUrlParam('reportingStatus', value);
        },
        [updateUrlParam]
    );

    const setAddressFilter = useCallback(
        (value: string) => {
            setAddressFilterState(value);
            updateUrlParam('address', value);
        },
        [updateUrlParam]
    );

    return {
        // State
        locationName,
        statusFilter,
        reportingStatusFilter,
        addressFilter,
        // Setters
        setLocationName,
        setStatusFilter,
        setReportingStatusFilter,
        setAddressFilter,
        // Key for detecting changes
        filtersKey
    };
};
